import { Component, computed, input, linkedSignal, output } from '@angular/core'
import { MatIconModule } from '@angular/material/icon'
import { RichTextComponent } from '../rich-text/rich-text.component'
import { AnnouncementVariant, hasRichText } from './announcement.model'

let nextId = 0

/**
 * A premise's announcement ("Upozornenie na stránke"), in either look: `variant="callout"` (a card, the public page)
 * or `variant="bar"` (a full-width strip). Both behave the same: the plain-text heading is always shown, "Zobraziť
 * viac" reveals the rich text only when there is some, and × (only with `dismissible`, off for now) emits
 * `dismissed` - the parent decides whether to remember it (`AnnouncementDismissal`) and hides the banner.
 *
 * A host site restyles it through the `--seiri-announcement-*` custom properties, which reach into an element's
 * shadow root; docs/announcements.md lists them. `content` was sanitised by the API, and `seiri-rich-text` runs it
 * through Angular's sanitiser again.
 */
@Component({
  selector: 'seiri-announcement-banner',
  imports: [MatIconModule, RichTextComponent],
  templateUrl: './announcement-banner.component.html',
  styleUrl: './announcement-banner.component.css',
  host: {
    class: 'seiri-announcement-banner',
    '[class.seiri-announcement-banner--bar]': "variant() === 'bar'",
    '[class.seiri-announcement-banner--callout]': "variant() === 'callout'",
  },
})
export class AnnouncementBannerComponent {
  /** The plain-text title. Named `heading`, not `title`, so it never becomes the host's tooltip. */
  readonly heading = input.required<string>()
  /** Sanitised rich text; `''` when there is none. */
  readonly content = input('')
  readonly variant = input<AnnouncementVariant>('callout')
  /** Starts with the rich text open. Visitors always get it closed; the editor's preview opens it to show it all. */
  readonly startExpanded = input(false)
  /**
   * Shows × (`dismissed`). Off everywhere since 2026-10-18: dismissing is disabled for now, with its code kept
   * (`AnnouncementDismissal`, the parents' handlers) so turning it back on is setting this. docs/announcements.md.
   */
  readonly dismissible = input(false)
  readonly dismissed = output<void>()

  protected readonly expanded = linkedSignal(() => this.startExpanded())
  protected readonly collapsible = computed(() => hasRichText(this.content()))
  protected readonly toggleLabel = computed(() => (this.expanded() ? 'Skryť' : 'Zobraziť viac'))

  private readonly id = nextId++
  protected readonly headingId = `seiri-announcement-heading-${this.id}`
  protected readonly detailId = `seiri-announcement-detail-${this.id}`

  protected toggle(): void {
    this.expanded.update((expanded) => !expanded)
  }
}
